import axios from 'axios';
import * as v1Endpoints from './v1/index.js';
import * as v3Endpoints from './v3/index.js';

const V1_PATH_PREFIX = 'v3/v1';
const V3_PATH_PREFIX = 'v3';

const mapValues = (fn, obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, fn(v)]));

export const V1 = ({ token, generateToken, stage = 'prod', port, ...axiosOptions } = {}) => {
    const client = createInstance({
        token,
        generateToken,
        stage,
        port,
        pathPrefix: V1_PATH_PREFIX,
        ...axiosOptions,
    });
    const initializedEndpoints = mapValues(endpoint => endpoint(client), v1Endpoints);
    const {
        clearContentCaches,
        createPOI,
        getAiJson,
        getAiJsonForLocale,
        getBoundingBoxes,
        getBoundsPolygons,
        getContent,
        getContentVenueAccountTree,
        getFullVenueData,
        getGeoJson,
        getMapLocaleTableFile,
        getNavigation,
        getSvg,
        getSvgForLocale,
        getVenueCategories,
        getVenueDataObject,
        getVenueDynamicContent,
        listAccountsWithCustomColors,
        listAccountsWithCustomPOIs,
        listAccountsWithCustomVenueDetails,
        listBeacons,
        listContent,
        listPOIs,
        listPOIsForAccount,
        listPOIsForLocale,
        listVenuesForAccount,
        openLocation,
        putVenueDataObject,
        updateAiJson,
        updateNavGraph,
        updatePOIs,
    } = initializedEndpoints;

    return {
        options: {
            accessToken: token,
            generateToken,
            stage,
            ...axiosOptions,
        },
        clearContentCaches,
        createPOI,
        getAiJson,
        getAiJsonForLocale,
        getBoundingBoxes,
        getBoundsPolygons,
        getContent,
        getContentVenueAccountTree,
        getFullVenueData,
        getGeoJson,
        getMapLocaleTableFile,
        getNavigation,
        getSvg,
        getSvgForLocale,
        getVenueCategories,
        getVenueDataObject,
        getVenueDynamicContent,
        listAccountsWithCustomColors,
        listAccountsWithCustomPOIs,
        listAccountsWithCustomVenueDetails,
        listBeacons,
        listContent,
        listPOIs,
        listPOIsForAccount,
        listPOIsForLocale,
        listVenuesForAccount,
        openLocation,
        putVenueDataObject,
        updateAiJson,
        updateNavGraph,
        updatePOIs,
    };
};

export const V3 = ({ token, generateToken, stage = 'prod', port, ...axiosOptions } = {}) => {
    const client = createInstance({
        token,
        generateToken,
        stage,
        port,
        pathPrefix: V3_PATH_PREFIX,
        ...axiosOptions,
    });
    const initializedEndpoints = mapValues(endpoint => endpoint(client), v3Endpoints);
    const {
        addVenueIdIsMigrated,
        venueIdIsMigrated,
        listVenueIds,
        describeVenue,
        getBoundingBoxes,
        getBoundsPolygons,
        getEntities,
        getFeatures,
        putEntities,
        putFeaturesAndEntities,
        putFeaturesFromIllustrator,
        tagMap,
        pushThemeToContent,
        renderNavGraphForAiJson,
        validateMapData,
        getVenueConfiguration,
        putVenueConfiguration,
        getVenueConfigurationWithAccount,
        putVenueConfigurationWithAccount,
        putAccountWithCustomVenueData,
        listAccountsWithCustomVenueData,
        listAccountsWithCustomColors,
        putVenueDataObject,
        getVenueDataObject,
        getAiJson,
        rmsv1GetAiJson,
        putAiJson,
        listVenuesForAccount,
    } = initializedEndpoints;

    return {
        options: {
            accessToken: token,
            generateToken,
            stage,
            ...axiosOptions,
        },
        addVenueIdIsMigrated,
        venueIdIsMigrated,
        listVenueIds,
        describeVenue,
        getBoundingBoxes,
        getBoundsPolygons,
        getEntities,
        getFeatures,
        putEntities,
        putFeaturesAndEntities,
        putFeaturesFromIllustrator,
        tagMap,
        pushThemeToContent,
        renderNavGraphForAiJson,
        validateMapData,
        getVenueConfiguration,
        putVenueConfiguration,
        getVenueConfigurationWithAccount,
        putVenueConfigurationWithAccount,
        putAccountWithCustomVenueData,
        listAccountsWithCustomVenueData,
        listAccountsWithCustomColors,
        putVenueDataObject,
        getVenueDataObject,
        getAiJson,
        rmsv1GetAiJson,
        putAiJson,
        listVenuesForAccount,
    };
};

// Create an instance of axios client used to send the final request to api endpoint.
// @sig (String, String, Object) => AxiosClient
const createInstance = ({ token, generateToken, stage, port, pathPrefix, ...axiosOptions }) => {
    if (!token && !generateToken) throw new Error('Either "token" or "generateToken" must be specified');
    const instance = axios.create({
        baseURL: getBaseURL({ stage, port, pathPrefix }),
        validateStatus: () => true,
        ...axiosOptions,
    });
    // Add a request interceptor, so we can attach a AUTH TOKEN before sending the request.
    instance.interceptors.request.use(addAuthenticationInterceptor({ token, generateToken }));
    return instance;
};

// Calculate the base url for the client given the stage, port, and pathPrefix
// @sig (Options) -> String
export const getBaseURL = ({ stage, port, pathPrefix }) =>
    pathPrefix ? `${getDomainName({ stage, port })}/${pathPrefix}` : getDomainName({ stage, port });

const getDomainName = ({ stage, port } = {}) => {
    if (port) {
        return `http://localhost:${port}`;
    } else if (stage === 'staging') {
        return 'https://staging.rms.locuslabs.com';
    } else if (stage === 'dev') {
        return 'https://dev.rms.locuslabs.com';
    } else {
        return 'https://rms.locuslabs.com';
    }
};

/**
 * Fetches authntication headers and adds them the to request.
 *
 * @param {*} username
 * @param {*} password
 */
export const addAuthenticationInterceptor =
    ({ token, generateToken }) =>
    async requestConfig => {
        const authorizationToken = generateToken ? await generateToken() : token;
        const headers = {
            ...requestConfig.headers,
            'x-api-key': authorizationToken,
        };
        return { ...requestConfig, headers };
    };
